import { readPref, removePref, writePref } from '../prefs';

/**
 * État « jeu complet » de l'appareil (A112) — **pur**, testé dans
 * `tests/ui/entitlement.test.ts`.
 *
 * Il vit dans `localStorage` (clé `diceordie.entitlement`), **jamais dans la
 * sauvegarde** : c'est l'appareil qui est ouvert, pas la partie. Il n'est donc
 * effacé ni par « Nouvelle partie » ni par l'import d'une sauvegarde. Sur
 * Android, Google Play le restaure sur un nouvel appareil (`play.ts`).
 *
 * Le jeton n'est **pas vérifié** (A112-4 : vérification côté client seulement) :
 * il sert de trace (jeton d'achat Play, jeton du Worker pour un code).
 */

export const ENTITLEMENT_KEY = 'diceordie.entitlement';

/** D'où vient l'ouverture : achat Google Play, code maison, ou debug. */
export type EntitlementSource = 'play' | 'code' | 'debug';

export interface Entitlement {
  source: EntitlementSource;
  /** Jeton d'achat Play ou jeton du Worker (code), absent pour `debug`. */
  token?: string;
  /** Date de l'ouverture, ISO UTC. */
  at: string;
}

const SOURCES: readonly EntitlementSource[] = ['play', 'code', 'debug'];

type Listener = (entitlement: Entitlement | null) => void;
const listeners = new Set<Listener>();

function isSource(v: unknown): v is EntitlementSource {
  return typeof v === 'string' && (SOURCES as readonly string[]).includes(v);
}

/** Lecture tolérante : valeur absente, illisible ou d'une autre forme → `null`. */
export function readEntitlement(): Entitlement | null {
  const raw = readPref(ENTITLEMENT_KEY);
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return null;
    const p = parsed as Record<string, unknown>;
    if (!isSource(p.source)) return null;
    const at = typeof p.at === 'string' && p.at ? p.at : new Date(0).toISOString();
    const out: Entitlement = { source: p.source, at };
    if (typeof p.token === 'string' && p.token) out.token = p.token.slice(0, 4096);
    return out;
  } catch {
    return null;
  }
}

function notify(value: Entitlement | null): void {
  for (const fn of Array.from(listeners)) {
    try {
      fn(value);
    } catch {
      /* un abonné défaillant ne doit jamais gêner l'écriture */
    }
  }
}

/** Enregistre l'ouverture de l'appareil et prévient les abonnés. */
export function writeEntitlement(e: Entitlement): void {
  const clean: Entitlement = { source: e.source, at: e.at };
  if (e.token) clean.token = e.token;
  writePref(ENTITLEMENT_KEY, JSON.stringify(clean));
  notify(clean);
}

/** Referme l'appareil (remboursement, debug) et prévient les abonnés. */
export function clearEntitlement(): void {
  removePref(ENTITLEMENT_KEY);
  notify(null);
}

/** S'abonne aux changements ; renvoie le désabonnement. */
export function onEntitlementChange(fn: Listener): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}
